import { Box, Chip, Grid, Typography, makeStyles } from '@material-ui/core';
import { InfoCard, Link, Progress } from '@backstage/core-components';
import { stringifyEntityRef } from '@backstage/catalog-model';
import { useEntity } from '@backstage/plugin-catalog-react';
import { Severity, useEntityDependencyAlerts } from '../health/api';

const SEVERITY_STYLE: Record<Severity, { label: string; color: string }> = {
  critical: { label: 'critical', color: '#E5383B' },
  high: { label: 'high', color: '#E07A2E' },
  medium: { label: 'medium', color: '#F2A900' },
  low: { label: 'low', color: '#8A8FA8' },
};

const SEVERITY_ORDER: Severity[] = ['critical', 'high', 'medium', 'low'];

const useStyles = makeStyles(theme => ({
  counts: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(1.5),
  },
  row: {
    padding: theme.spacing(0.5, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': { borderBottom: 'none' },
  },
  head: { display: 'flex', gap: theme.spacing(1), alignItems: 'baseline' },
  dot: { fontSize: 10 },
  pkg: { fontFamily: 'monospace', fontSize: 13 },
  meta: { color: theme.palette.text.secondary, fontSize: 12 },
}));

const SHOWN = 6;

export const DependencyAlertsCard = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const { value, error, loading } = useEntityDependencyAlerts(
    stringifyEntityRef(entity),
  );

  if (value && !value.available) return null;

  let content;
  if (loading) {
    content = <Progress />;
  } else if (!value) {
    content = (
      <Typography color="error">
        Не удалось получить данные: {error?.message}
      </Typography>
    );
  } else if (!value.alerts.length) {
    content = (
      <Typography className={classes.meta}>Открытых уязвимостей нет</Typography>
    );
  } else {
    content = (
      <>
        <Box className={classes.counts}>
          {SEVERITY_ORDER.filter(s => value.counts[s]).map(s => (
            <Chip
              key={s}
              size="small"
              variant="outlined"
              label={`${SEVERITY_STYLE[s].label}: ${value.counts[s]}`}
              style={{
                borderColor: SEVERITY_STYLE[s].color,
                color: SEVERITY_STYLE[s].color,
              }}
            />
          ))}
        </Box>
        {value.alerts.slice(0, SHOWN).map(a => (
          <div key={a.number} className={classes.row}>
            <div className={classes.head}>
              <span
                className={classes.dot}
                style={{ color: SEVERITY_STYLE[a.severity].color }}
              >
                ●
              </span>
              <Link to={a.url} className={classes.pkg}>
                {a.package}
              </Link>
              <span className={classes.meta}>
                {a.vulnerableRange}
                {a.patchedVersion ? ` → ${a.patchedVersion}` : ''}
              </span>
            </div>
            <div className={classes.meta}>{a.summary}</div>
          </div>
        ))}
      </>
    );
  }

  const total = value?.alerts.length ?? 0;
  return (
    <Grid item md={6} xs={12}>
      <InfoCard
        title="Уязвимости зависимостей"
        subheader={total > SHOWN ? `Показаны ${SHOWN} из ${total}` : undefined}
        deepLink={
          value?.available
            ? {
                title: 'Все в Dependabot',
                link: `https://github.com/${value.repository}/security/dependabot`,
              }
            : undefined
        }
      >
        {content}
      </InfoCard>
    </Grid>
  );
};
